import { getLastDateOfMonth } from "./dateTime";
import { MONTH_NAMES, ROW_TYPES } from "../constants";
import Decimal from "decimal.js";

/**
 *
 * @param {string} street_address
 * @param {string} suburb
 * @param {string} city
 * @param {string} postal_code
 * @param {string} state
 * @param {string} country
 * @returns {string} - The formatted address string.
 */
export function capitalizeAddress(
  street_address,
  suburb,
  city,
  postal_code,
  state,
  country,
) {
  let data = [];
  street_address && data.push(street_address);
  suburb && data.push(suburb);
  city && data.push(city);
  postal_code && data.push(postal_code);
  state && data.push(state);
  country && data.push(country);
  let str = data.join(", ");
  return str.replace(/(^|\s)([a-z])/g, function (m, p1, p2) {
    return p1 + p2.toUpperCase();
  });
}

/**
 * Converts a byte value into a human-readable string with appropriate units.
 *
 * @param {number} bytes - The size in bytes.
 * @returns {string} - The formatted size with units.
 */
export function bytesToSize(bytes) {
  if (bytes === 0) return "0 B";
  if (bytes === null || bytes === undefined || bytes === "") return "";
  const k = new Decimal(1024);
  const bytesDec = new Decimal(bytes);
  let sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Decimal.log(bytesDec).div(Decimal.log(k)).floor().toNumber();
  const size = bytesDec.div(k.pow(i)).toDecimalPlaces(0);
  return `${size} ${sizes[i]}`;
}

/**
 * Removes trailing zeros from a number, ensuring up to 2 decimal precision.
 *
 * @param {number|string} num - The number to process.
 * @returns {string} - The formatted number without trailing zeros.
 */
export function removeTrailingZeros(num) {
  // remove trailing zeros from a number and at most 2 precision
  return new Decimal(num)
    .toFixed(2)
    .replace(/([0-9]+(\.[0-9]+[1-9])?)(\.?0+$)/, "$1");
}

/**
 * Converts a string to title case.
 *
 * @param {string} str - The input string.
 * @returns {string} - The string converted to title case.
 */
export const toTitleCase = (str) => {
  if (!str || typeof str !== "string") {
    return "";
  }
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

/**
 * Sorts an array of objects alphabetically by their 'label' property.
 *
 * @param {Object} a - First object to compare.
 * @param {Object} b - Second object to compare.
 * @returns {number} - Comparison result.
 */
export function sortByLabel(a, b) {
  return a.label.toString().toLowerCase() > b.label.toString().toLowerCase()
    ? 1
    : b.label.toString().toLowerCase() > a.label.toString().toLowerCase()
      ? -1
      : 0;
}

/**
 * Creates a custom sorter for an array of objects based on a specified key.
 *
 * @param {string} name - The key to sort by.
 * @returns {Function} - A comparator function.
 */
export function sortByOption(name) {
  return function (a, b) {
    var result =
      a[name].toString().toLowerCase().trim() >
      b[name].toString().toLowerCase().trim()
        ? 1
        : b[name].toString().toLowerCase().trim() >
            a[name].toString().toLowerCase().trim()
          ? -1
          : 0;
    return result;
  };
}
/**
 *
 * @param {string} a
 * @param {string} b
 * @returns {number} - Returns 1 if a > b, -1 if b > a, or 0 if equal.
 */
export function alphabeticalSort(a, b) {
  return a.toString().toLowerCase() > b.toString().toLowerCase()
    ? 1
    : b.toString().toLowerCase() > a.toString().toLowerCase()
      ? -1
      : 0;
}

/**
 * Determines the appropriate sorting function based on the column name.
 *
 * @param {string} name - Name of the column to sort.
 * @param {string} order - Sorting order ('asc' or 'desc').
 * @returns {function} - A sorting function for the specified column.
 */
export function handleTableSorter(name, order) {
  const string = ["unit", "label", "description", "created_date", "sort_id"];
  if (string.includes(name) && name !== "sort_id") {
    return sorter["string"](order, name);
  } else {
    return sorter["number"](order, name);
  }
}
const sorter = {
  /**
   * Sorting function for numeric columns.
   *
   * @param {string} order - Sorting order ('asc' or 'desc').
   * @param {string} name - Column name to sort.
   * @returns {function} - Numeric sorting function.
   */
  number: (order, name) => (a, b) => {
    if (Object.keys(a).includes(name) && a[name] !== null)
      return order === "asc" ? a[name] - b[name] : b[name] - a[name];
  },
  /**
   * Sorting function for string columns.
   *
   * @param {string} order - Sorting order ('asc' or 'desc').
   * @param {string} name - Column name to sort.
   * @returns {function} - String sorting function.
   */
  string: (order, name) => (a, b) => {
    if (Object.keys(a).includes(name) && a[name] !== null)
      return order === "asc"
        ? a[name].localeCompare(b[name])
        : b[name].localeCompare(a[name]);
  },
};

/**
 * Removes all elements with a specified class name from the DOM.
 *
 * @param {string} className - Class name of the elements to remove.
 */
export function removeElementsByClass(className) {
  const elements = document.getElementsByClassName(className);
  while (elements.length > 0) {
    elements[0].parentNode.removeChild(elements[0]);
  }
}

/**
 * Validates if a given string is a valid email address.
 *
 * @param {string} email - Email address to validate.
 * @returns {boolean} - True if valid, false otherwise.
 */
export function validateEmailAddress(email) {
  var re =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return re.test(email);
}

/**
 * Generates an auto-generated reference number based on monthly data.
 *
 * @param {Object} MonthAndCounts - Object mapping month-year strings to counts.
 * @returns {string} - Generated reference number.
 */
export const getAutoReferenceNumber = (MonthAndCounts) => {
  const today = new Date();
  const month = today.getMonth();
  const year = today.getFullYear();

  // If no stat available
  if (!MonthAndCounts || Object.keys(MonthAndCounts).length === 0) {
    return `${MONTH_NAMES[month]} ${year}`;
  }

  // Get the latest month and year
  const latestMonthYear = Object.keys(MonthAndCounts).sort((a, b) => {
    const date1 = getLastDateOfMonth(a);
    const date2 = getLastDateOfMonth(b);
    return date2 - date1;
  })[0];

  const lastDayOfCurrentMonth = new Date(year, month + 1, 0);

  // If the latest month and year is greater
  if (lastDayOfCurrentMonth > getLastDateOfMonth(latestMonthYear)) {
    return `${MONTH_NAMES[month]} ${year}`;
  }

  const monthNumber = parseInt(latestMonthYear.split("-")[0]);

  let lastCount = MonthAndCounts[latestMonthYear] + 1;
  return `${MONTH_NAMES[monthNumber - 1]} ${year} (${lastCount})`;
};

/**
 * Checks if a target string is included in a source string (case-insensitive).
 *
 * @param {string} source - The source string.
 * @param {string} target - The target string to search for.
 * @returns {boolean} - True if target is found, false otherwise.
 */
export const caseInsensitiveIncludes = (source, target) => {
  const sourceString = String(source ?? "").toLowerCase();
  const targetString = String(target).toLowerCase();

  return sourceString.includes(targetString);
};

/**
 * Finds the key in an object by its value.
 *
 * @param {Object} obj - Object to search.
 * @param {any} findKey - Value to find the corresponding key for.
 * @returns {string|null} - Found key or null if not found.
 */
export const findKeyByValue = (obj, findKey) => {
  if (!obj) {
    return null;
  }
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key].value;
      if (value === findKey) {
        return key;
      }
    }
  }
  return null;
};

/**
 * Checks if an object is empty.
 *
 * @param {Object} obj - Object to check.
 * @returns {boolean} - True if empty, false otherwise.
 */
export const isObjectEmpty = (obj) => {
  return Object.keys(obj).length === 0;
};

/**
 * Smoothly scrolls the page to the component referenced by `targetRef`.
 *
 * @param {Object} targetRef - A React ref object pointing to the target DOM element.
 * @example
 * const myRef = useRef(null);
 * scrollToComponent(myRef);
 */
export const scrollToComponent = (targetRef) => {
  if (targetRef && targetRef.current) {
    targetRef.current.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center",
    });
  }
};

/**
 * Recursively injects additional rows (e.g., description/details rows) into a nested data structure.
 *
 * @param {Array} data - The input array of data items, each potentially containing childitems.
 * @param {Array} fields - An array of field names to include in the additional "DETAILS" row.
 * @param {(item: Object) => boolean} [callback] - A function to check if the item meets a certain condition to skip generating aditional row.
 * @returns {Array} A new array where each original item is followed by an injected DETAILS row.
 */
export const injectAdditionalRows = (data, fields, callback = (_) => false) => {
  return data.flatMap((item) => {
    // Clone the item to avoid mutating original
    const cloned = { ...item };

    // Process child items recursively (if they exist)
    if (cloned.childitems && Array.isArray(cloned.childitems)) {
      cloned.childitems = injectAdditionalRows(
        cloned.childitems,
        fields,
        callback,
      );
    }

    // Skip adding additional rows for items that meet the condition.
    if (callback(item)) {
      return [cloned];
    }

    // Build the DETAILS row dynamically based on fields
    const detailsRow = {
      id: `${cloned.id}-desc`,
      rowType: ROW_TYPES.DETAILS,
      parent_id: cloned.id,
    };

    fields.forEach((field) => {
      detailsRow[field] = cloned[field];
    });

    // Return the MAIN and DETAILS rows
    return [{ ...cloned, rowType: ROW_TYPES.MAIN }, detailsRow];
  });
};
